import {
  AiCreateArtworkModel,
  AIModelTabEnum,
  CopyrightAiSearchContent,
  CopyrightAiSearchLanguage,
  CopyrightAiSearchScope,
  CopyrightAiSort,
  getTabs_aiArtworkResourceModule_aiSquareTab,
  getTabs_aiArtworkResourceModule_aiSquareTab_tab_child,
  SquarePostType,
} from '~/graphqls/types'

const sortMenuMap = {
  推荐: CopyrightAiSort.COMPREHENSIVE,
  热门: CopyrightAiSort.HOT,
  最新: CopyrightAiSort.LATEST,
  同款最多: CopyrightAiSort.SAME_MOST,
  点赞最多: CopyrightAiSort.LIKE_COUNT,
  收藏最多: CopyrightAiSort.COLLECT_COUNT,
}

const scopeMap = {
  全网: undefined,
  '无界词/图': CopyrightAiSearchScope.AI_SQUARE,
}

const contentMap = {
  全部: undefined,
  '标题/简介/画面描述': CopyrightAiSearchContent.PROMPT,
  图像信息: CopyrightAiSearchContent.IMAGE,
}

const langMap = {
  不限: undefined,
  中文: CopyrightAiSearchLanguage.CHINESE,
  英文: CopyrightAiSearchLanguage.ENGLISH,
}

/** 字符串转布尔值 */
const strToBoolMap = {
  不限: undefined,
  有: true,
  无: false,
}

const modelMap = {
  全部: undefined,
  通用: AiCreateArtworkModel.STABLE_DIFFUSION,
  漫画: AiCreateArtworkModel.ANIME_DIFFUSION,
  风格: AiCreateArtworkModel.STYLE,
  MJ: AiCreateArtworkModel.MIDJOURNEY,
}

type Tag0 =
  | Omit<getTabs_aiArtworkResourceModule_aiSquareTab_tab_child, 'child'>
  | getTabs_aiArtworkResourceModule_aiSquareTab_tab_child
export type Tag = Tag0 & { pid?: string | null }

export function genMapFromTabs(
  tabs: getTabs_aiArtworkResourceModule_aiSquareTab
) {
  let idMap = new Map<string, Tag>()

  const recursive = (item: Tag | null, parent: Tag | null) => {
    if (!item) return
    /** link to parent, 这里必须用id，不知道是node还是nextjs不允许循环的object */
    item.pid = parent?.idStr || '-1'
    idMap.set(item.idStr!, item)
    if (!(item as any).child) {
      ;(item as any).child = []
    }
    // 广场标签
    if (item.tabType === AIModelTabEnum.SQUARE_CREATE_TAG) {
      return
    }
    if ((item as any).child.length === 0) {
      return
    }
    ;(item as any).child.map((i: any) => {
      recursive(i, item)
    })
  }
  recursive(tabs.tab, null)
  return idMap
}

// 导出默认值给 useSearchParams 用
export const DefaultSort = '推荐'
export const DefualtScope = '无界词/图'
export const DefaultContent = '全部'
export const DefaultLang = '不限'
export const DefaultInitImage = '不限'
export const DefaultCharacter = '不限'
export const DefaultModelFusion = '不限'

export function getSearchParams(
  query: Record<string, string | undefined | string[]>,
  tabs: getTabs_aiArtworkResourceModule_aiSquareTab
) {
  const keyword = String(query.keywords || '')
  const sort = String(query.sort || '') || DefaultSort
  const scope = String(query.scope || '') || DefualtScope
  const content = String(query.content || '') || DefaultContent
  const lang = String(query.lang || '') || DefaultLang
  const initImage = String(query.initImage || '') || DefaultInitImage
  const character = String(query.character || '') || DefaultCharacter
  const modelFusion = String(query.modelFusion || '') || DefaultModelFusion
  /** 选中的artist id */
  const artistid = String(query.artistid || '') || undefined
  /** 选中的主题。themeid包含两个部分，
   * 1. 非风格模型的情况下，themeid代表广场的分类。
   * 2. 风格模型下，themeid代表风格模型的id
   */
  let themeid = query.themeid || undefined
  /** 选中的leaf */
  const activeLeaf = String(query.leaf || '')
    .split('.')
    .map(item => item)
    .filter(item => !!item)
  /** 选中的L1 tab */
  const l_1 = String(query.l_1 || '')

  const idMap = genMapFromTabs(tabs)

  let model = modelMap[l_1 as keyof typeof modelMap]
  let myAttention = false
  if (l_1 === '关注') {
    model = undefined
    myAttention = true
  }
  if (l_1 === '工作流') {
    model = undefined
  }
  /** 风格模型 */
  let styleModelKey: string | undefined = undefined
  if (model === AiCreateArtworkModel.STYLE) {
    styleModelKey = themeid as string
    themeid = undefined
  }

  const cTag: string[] = []
  const eTag: string[] = []
  const curArtist = idMap.get(artistid || '-1')

  // 填入作者标签
  if (curArtist) {
    cTag.push(curArtist.name || '')
    eTag.push(curArtist.englishName || '')
  }

  // 填入叶子标签
  activeLeaf.forEach(id => {
    const item = idMap.get(id)
    if (item) {
      cTag.push(item.name || '')
      eTag.push(item.englishName || '')
    }
  })

  /** 选中的尺寸 */
  const size = String(query.size || '') || undefined
  /** 选中的可见范围 */
  const visibleType =
    query.visibleType === '0' ? 0 : Number(query.visibleType) || undefined

  /** 是否有底图 */
  let _initImage = strToBoolMap[initImage as keyof typeof strToBoolMap]
  /** 是否使用了角色同人 */
  let _character = strToBoolMap[character as keyof typeof strToBoolMap]
  /** 是否使用了融合模型 */
  let _modelFusion = strToBoolMap[modelFusion as keyof typeof strToBoolMap]
  /** 选中的模型类型的modelCode */
  let _modelType = String(query.modeltype || '') || undefined

  // 风格模型下面没有modelCode 和 底图 筛选项
  if (model === AiCreateArtworkModel.STYLE) {
    _initImage = undefined
    _modelType = undefined
  }

  /** themeid 代表特殊情况：合集 */
  if (themeid === '-1') {
    return {
      sort: sortMenuMap[sort as keyof typeof sortMenuMap],
      squarePostType: SquarePostType.AI_COLLECTION,
      chineseTags: [],
      englishTags: [],
    }
  }

  return {
    sort: sortMenuMap[sort as keyof typeof sortMenuMap],
    scope: scopeMap[scope as keyof typeof scopeMap],
    content: contentMap[content as keyof typeof contentMap],
    language: langMap[lang as keyof typeof langMap],
    initImage: _initImage,
    themeId: themeid,
    chineseTags: Array.from(new Set(cTag)),
    englishTags: Array.from(new Set(eTag)),
    keyword,
    model,
    /** 我的关注 */
    myAttention,
    size,
    modelClassificationIds: _modelType?.split(','),
    visibleType,
    styleModelKey,
    character: _character,
    modelFusion: _modelFusion,
  }
}

/**  是否显示搜索结果的数量 */
export const showSearchResultCount = (
  params: ReturnType<typeof getSearchParams>
) => {
  if (
    params.sort === CopyrightAiSort.COMPREHENSIVE ||
    params.sort === CopyrightAiSort.LATEST
  ) {
    return false
  }
  // 如果搜索范围是ai广场不展示数量
  if (!params.scope) {
    return true
  }
  if (
    params.content ||
    params.language ||
    params.initImage ||
    params.character ||
    params.modelFusion ||
    params.themeId ||
    params.keyword ||
    params.model ||
    params.size ||
    params.modelClassificationIds ||
    params.visibleType ||
    params.styleModelKey
  ) {
    return true
  }
  if (params.chineseTags.length !== 0) {
    return true
  }
  if (params.englishTags.length !== 0) {
    return true
  }
  if (params.myAttention) {
    return true
  }
  if (params.squarePostType) {
    return true
  }

  return false
}

export function isSearchParamsSame(
  a: ReturnType<typeof getSearchParams>,
  b: ReturnType<typeof getSearchParams>
) {
  return JSON.stringify(a) === JSON.stringify(b)
}
